type DateParts = {
  year: number;
  month: number;
  day: number;
};

function isValidDatePart(year: number, month: number, day: number): boolean {
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

function formatDatePart(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function parseDateParts(value: string): DateParts | null {
  const trimmed = value.trim();

  const isoMatch = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(trimmed);
  if (isoMatch) {
    const [, yearPart, monthPart, dayPart] = isoMatch;
    const year = Number(yearPart);
    const month = Number(monthPart);
    const day = Number(dayPart);
    return isValidDatePart(year, month, day) ? { year, month, day } : null;
  }

  const slashMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (slashMatch) {
    const [, monthPart, dayPart, yearPart] = slashMatch;
    const year = Number(yearPart);
    const month = Number(monthPart);
    const day = Number(dayPart);
    return isValidDatePart(year, month, day) ? { year, month, day } : null;
  }

  return null;
}

function formatDateParts(parts: DateParts): string {
  return formatDatePart(parts.year, parts.month, parts.day);
}

function normalizeTimePart(hourPart: string, minutePart: string, secondPart?: string): string | null {
  const hour = Number(hourPart);
  const minute = Number(minutePart);
  const second = secondPart === undefined ? undefined : Number(secondPart);

  if (
    !Number.isInteger(hour) ||
    !Number.isInteger(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59 ||
    (second !== undefined && (!Number.isInteger(second) || second < 0 || second > 59))
  ) {
    return null;
  }

  const hhmm = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  return second === undefined ? hhmm : `${hhmm}:${String(second).padStart(2, '0')}`;
}

function normalizeMeridiemTimePart(
  hourPart: string,
  minutePart: string,
  secondPart: string | undefined,
  meridiemPart: string
): string | null {
  const hour = Number(hourPart);
  if (!Number.isInteger(hour) || hour < 1 || hour > 12) return null;

  const meridiem = meridiemPart.toUpperCase();
  const normalizedHour = meridiem === 'AM' ? hour % 12 : hour === 12 ? 12 : hour + 12;
  return normalizeTimePart(String(normalizedHour), minutePart, secondPart);
}

export function normalizeDateInputValue(value: string): string {
  const parts = parseDateParts(value);
  return parts ? formatDateParts(parts) : value;
}

export function normalizeDateTimeLocalInputValue(value: string): string {
  const dateOnly = parseDateParts(value);
  if (dateOnly) return `${formatDateParts(dateOnly)}T00:00`;

  const trimmed = value.trim();
  const dateTimeMatch =
    /^(\d{4})-(\d{1,2})-(\d{1,2})[T ](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/.exec(trimmed);
  if (dateTimeMatch) {
    const [, yearPart, monthPart, dayPart, hourPart, minutePart, secondPart] = dateTimeMatch;
    const year = Number(yearPart);
    const month = Number(monthPart);
    const day = Number(dayPart);
    if (!isValidDatePart(year, month, day)) return value;

    const timePart = normalizeTimePart(hourPart, minutePart, secondPart);
    if (!timePart) return value;

    return `${formatDatePart(year, month, day)}T${timePart}`;
  }

  const usDateTimeMatch =
    /^(\d{1,2})\/(\d{1,2})\/(\d{4}),?\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?\s*(AM|PM)$/i.exec(
      trimmed
    );
  if (!usDateTimeMatch) return value;

  const [
    ,
    usMonthPart,
    usDayPart,
    usYearPart,
    usHourPart,
    usMinutePart,
    usSecondPart,
    meridiemPart,
  ] = usDateTimeMatch;
  const usYear = Number(usYearPart);
  const usMonth = Number(usMonthPart);
  const usDay = Number(usDayPart);
  if (!isValidDatePart(usYear, usMonth, usDay)) return value;

  const usTimePart = normalizeMeridiemTimePart(
    usHourPart,
    usMinutePart,
    usSecondPart,
    meridiemPart
  );
  if (!usTimePart) return value;

  return `${formatDatePart(usYear, usMonth, usDay)}T${usTimePart}`;
}

export function normalizeTemporalInputValue(value: string, inputType: string | null): string {
  if (inputType === 'date') return normalizeDateInputValue(value);
  if (inputType === 'datetime-local') return normalizeDateTimeLocalInputValue(value);
  return value;
}
